/**
 * Sprint O4 — Payments history + quick add inside OrderDetailsModal.
 *
 * Reuses POST /api/orders/{id}/payments and DELETE .../payments/{pid}.
 * Calls onChanged() so the parent modal can re-fetch the order to pick up
 * the new pending_balance computed by the backend.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";

const METHODS = ["cash", "transfer", "zelle", "card", "other"];

const fmt = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PaymentsSection({ order, onChanged, disabled = false }) {
  const { t } = useI18n();
  const [adding, setAdding] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const payments = Array.isArray(order?.payments) ? order.payments : [];

  const reset = () => {
    setAmount("");
    setMethod("cash");
    setNote("");
    setAdding(false);
  };

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error(t("pay_amount_invalid"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders/${order.id}/payments`, {
        amount: amt,
        method,
        note,
      });
      toast.success(t("pay_ok"));
      reset();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (pid) => {
    if (!window.confirm(t("pay_delete_confirm"))) return;
    try {
      await axios.delete(`${API}/orders/${order.id}/payments/${pid}`);
      toast.success(t("pay_deleted_ok"));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error"));
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2 text-sm">
        <div className="border border-[var(--border)] p-2">
          <div className="label-overline">{t("pay_kpi_total")}</div>
          <div className="font-mono-tech font-bold">{fmt(order.total_amount)}</div>
        </div>
        <div className="border border-[var(--border)] p-2">
          <div className="label-overline">{t("pay_kpi_deposit_plus")}</div>
          <div className="font-mono-tech font-bold">
            {fmt((order.deposit_received || 0) + payments.reduce((a, p) => a + (p.amount || 0), 0))}
          </div>
        </div>
        <div className={`border-2 p-2 ${order.pending_balance > 0 ? "border-red-400 bg-red-50" : "border-emerald-400 bg-emerald-50"}`}>
          <div className="label-overline">{t("pay_kpi_pending")}</div>
          <div className="font-mono-tech font-bold">{fmt(order.pending_balance)}</div>
        </div>
      </div>

      {payments.length === 0 ? (
        <div className="text-xs text-[var(--text-secondary)] italic">{t("pay_none")}</div>
      ) : (
        <ul className="divide-y divide-[var(--border)] border border-[var(--border)]">
          {payments.map((p) => (
            <li
              key={p.id}
              data-testid={DASHBOARD.paymentsRow(p.id)}
              className="px-3 py-2 flex items-center justify-between gap-2 text-sm"
            >
              <div className="min-w-0">
                <div className="font-display font-bold">
                  {fmt(p.amount)}
                  <span className="ml-2 px-1.5 py-0.5 border border-[var(--border)] font-mono-tech text-[0.6rem] tracking-wider uppercase">
                    {p.method}
                  </span>
                </div>
                <div className="text-xs text-[var(--text-secondary)]">
                  {p.date ? p.date.slice(0, 16).replace("T", " ") : ""}
                  {p.note ? ` · ${p.note}` : ""}
                </div>
              </div>
              {!disabled && (
                <button
                  type="button"
                  data-testid={DASHBOARD.paymentsDelete(p.id)}
                  onClick={() => remove(p.id)}
                  className="text-xs underline text-red-700 hover:text-red-900"
                >
                  {t("delete")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!disabled && (
        adding ? (
          <div className="border border-[var(--border)] p-3 grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
            <div>
              <label className="label-overline block mb-1">{t("pay_field_amount")}</label>
              <input
                data-testid={DASHBOARD.paymentsAmount}
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full border border-[var(--border)] px-2 py-1.5 text-sm font-mono-tech"
              />
            </div>
            <div>
              <label className="label-overline block mb-1">{t("pay_field_method")}</label>
              <select
                data-testid={DASHBOARD.paymentsMethod}
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full border border-[var(--border)] px-2 py-1.5 text-sm font-mono-tech"
              >
                {METHODS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-overline block mb-1">{t("pay_field_note")}</label>
              <input
                data-testid={DASHBOARD.paymentsNote}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full border border-[var(--border)] px-2 py-1.5 text-sm"
              />
            </div>
            <div className="flex gap-1">
              <button
                type="button"
                data-testid={DASHBOARD.paymentsSubmit}
                disabled={submitting}
                onClick={submit}
                className="flex-1 bg-black text-white px-3 py-1.5 font-display font-bold text-sm disabled:opacity-50"
              >
                {submitting ? "…" : t("save")}
              </button>
              <button
                type="button"
                onClick={reset}
                className="border border-[var(--border)] px-3 py-1.5 font-display font-bold text-sm"
              >
                ×
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            data-testid={DASHBOARD.paymentsAddBtn}
            onClick={() => setAdding(true)}
            className="border-2 border-dashed border-[var(--border)] px-3 py-2 font-display font-bold text-sm w-full hover:bg-[var(--surface-alt)]"
          >
            {t("pay_add_btn")}
          </button>
        )
      )}
    </div>
  );
}
